/* eslint-disable import/no-extraneous-dependencies */
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Tabs, Tab, Box, Card } from '@mui/material';

import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import SellIcon from '@mui/icons-material/Sell';
import SummarizeIcon from '@mui/icons-material/Summarize';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import dayjs from 'dayjs';

import PropTypes from 'prop-types';
import * as actions from '../../redux/actions';
import Loading from '../../components/Loading';
import { Error } from '../../components/Error';
import { ViewTitle } from '../../components/ViewTitle';

import { BuyTab, SaleTab, SummaryTab, OtherTab } from './SummaryTabs';

// Tab Panel Component
const TabPanel = ({ children, value, index, ...other }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`simple-tabpanel-${index}`}
    aria-labelledby={`simple-tab-${index}`}
    {...other}
  >
    {value === index && <Box sx={{ p: 4 }}>{children}</Box>}
  </div>
);

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

// Main Summary Component
const Summary = ({ title, subtitle }) => {
  const dispatch = useDispatch();
  const goldTransaction = useSelector((state) => state.gold);
  const paymentOther = useSelector((state) => state.paymentOther);
  const me = useSelector((state) => state.me);
  const [loading, setLoading] = useState(true);
  const [allData, setAllData] = useState(false);
  const [date, setDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [tabValue, setTabValue] = useState(0);
  const history = useHistory();

  useEffect(() => {
    setLoading(true);
    dispatch(actions.goldTransactionAll({ date: date }));
    setLoading(false);
  }, [date, dispatch]);

  useEffect(() => {
    setLoading(true);
    dispatch(actions.paymentOtherAll({ date: date }));
    setLoading(false);
  }, [date, dispatch]);

  const buyOrder = goldTransaction?.rows?.filter(
    (item) => item?.gold_transaction_type === 'BUY',
  );
  const saleOrder = goldTransaction?.rows?.filter(
    (item) => item?.gold_transaction_type === 'SALE',
  );
  const otherOrders = paymentOther?.rows;

  const handleToInfoPage = async (id) => {
    history.push(`/gold/summary/detail/${id}`);
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Loading state
  if (goldTransaction.isLoading || !goldTransaction.rows) {
    return <Loading />;
  }

  // Success state
  if (!goldTransaction.isLoading && goldTransaction.isCompleted) {
    return (
      <Box sx={{ p: 2 }}>
        <ViewTitle title={title} subtitle={subtitle} />

        <Card sx={{ mt: 3, boxShadow: 3, borderRadius: 2 }}>
          <Box
            sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: '#f5f5f5' }}
          >
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                '& .MuiTab-root': {
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  py: 2,
                  minWidth: 'auto',
                },
              }}
            >
              <Tab
                icon={<ShoppingCartIcon />}
                iconPosition="start"
                label={`รายการซื้อ (${buyOrder?.length || 0})`}
              />
              <Tab
                icon={<SellIcon />}
                iconPosition="start"
                label={`รายการขาย (${saleOrder?.length || 0})`}
              />
              <Tab
                icon={<SummarizeIcon />}
                iconPosition="start"
                label={`รายการรวม (${
                  (buyOrder?.length || 0) + (saleOrder?.length || 0)
                })`}
              />
              <Tab
                icon={<MoreHorizIcon />}
                iconPosition="start"
                label={`รายการอื่นๆ (${otherOrders?.length || 0})`}
              />
            </Tabs>
          </Box>

          <TabPanel value={tabValue} index={0}>
            <BuyTab
              orders={buyOrder}
              date={date}
              setDate={setDate}
              allData={false}
              onViewDetail={handleToInfoPage}
            />
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <SaleTab
              orders={saleOrder}
              date={date}
              setDate={setDate}
              allData={false}
              onViewDetail={handleToInfoPage}
            />
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            <SummaryTab
              buyOrders={buyOrder}
              saleOrders={saleOrder}
              date={date}
              setDate={setDate}
              allData={true}
              onViewDetail={handleToInfoPage}
            />
          </TabPanel>

          <TabPanel value={tabValue} index={3}>
            <OtherTab
              orders={paymentOther?.rows}
              date={date}
              setDate={setDate}
              me={me}
              dispatch={dispatch}
              actions={actions}
            />
          </TabPanel>
        </Card>
      </Box>
    );
  }

  // Error state
  return <Error message={goldTransaction?.message} />;
};

Summary.propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.string,
};

Summary.defaultProps = {
  title: '',
  subtitle: '',
};

export default Summary;
